import { useEffect, useState } from "react";
import { AppLayout } from "@/components/AppLayout";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Landmark, Check, X } from "lucide-react";

const fmt = (n) => `$${Number(n || 0).toFixed(2)}`;

export default function AdminCredit() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [notes, setNotes] = useState({});
  const [busy, setBusy] = useState({});

  const load = async () => {
    try {
      const path = filter === "pending" ? "/admin/credit/pending" : `/admin/credit/all?status_filter=${filter}`;
      const { data } = await api.get(path);
      setItems(data.items || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, [filter]); // reload on filter change

  const decide = async (id, decision) => {
    setBusy(b => ({...b, [id]: true}));
    try {
      await api.post("/admin/credit/decide", { credit_id: id, decision, note: notes[id] || "" });
      toast.success(`Credit ${decision}d`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(b => ({...b, [id]: false})); }
  };

  return (
    <AppLayout variant="admin" requireAdmin>
      <div className="space-y-6" data-testid="admin-credit-page">
        <div className="flex items-center gap-2 text-xs text-[#EAB308] uppercase tracking-widest font-bold">
          <Landmark size={14}/> Credit Applications
        </div>
        <div className="flex gap-2 flex-wrap">
          {["pending","approved","rejected","expired","cleared","all"].map(f => (
            <button key={f} onClick={() => setFilter(f)}
              data-testid={`filter-${f}`}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${filter===f ? "bg-[#8AE234]/15 text-[#8AE234] border border-[#8AE234]/40" : "bg-white/5 text-slate-300 border border-white/10 hover:bg-white/10"}`}>
              {f}
            </button>
          ))}
        </div>

        <div className="nb-card p-4 overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead className="text-xs text-slate-400 uppercase tracking-widest">
              <tr>
                <th className="text-left py-2 px-2">User</th>
                <th className="text-right px-2">Amount</th>
                <th className="text-right px-2">Repaid</th>
                <th className="text-right px-2">Remaining</th>
                <th className="text-left px-2">Status</th>
                <th className="text-left px-2">Note / Action</th>
              </tr>
            </thead>
            <tbody className="text-slate-200">
              {items.map(it => (
                <tr key={it.id} className="border-t border-white/5" data-testid={`credit-row-${it.id}`}>
                  <td className="py-2 px-2 text-xs">{it.user_email}<div className="text-[10px] text-slate-500">{it.id.slice(0,8)}</div></td>
                  <td className="text-right px-2 font-mono">{fmt(it.amount)}</td>
                  <td className="text-right px-2 font-mono">{fmt(it.repaid)}</td>
                  <td className="text-right px-2 font-mono">{fmt(it.remaining)}</td>
                  <td className="px-2 text-xs uppercase">{it.status}</td>
                  <td className="px-2">
                    {it.status === "pending" ? (
                      <div className="flex items-center gap-2">
                        <input
                          placeholder="Note (optional)"
                          value={notes[it.id] || ""}
                          onChange={e => setNotes(n => ({...n, [it.id]: e.target.value}))}
                          className="nb-input py-1 text-xs w-40"
                          data-testid={`note-${it.id}`}
                        />
                        <button data-testid={`approve-${it.id}`} disabled={busy[it.id]} onClick={() => decide(it.id, "approve")}
                          className="px-2 py-1 rounded bg-[#8AE234]/15 text-[#8AE234] text-xs hover:bg-[#8AE234]/25 flex items-center gap-1">
                          <Check size={12}/> Approve
                        </button>
                        <button data-testid={`reject-${it.id}`} disabled={busy[it.id]} onClick={() => decide(it.id, "reject")}
                          className="px-2 py-1 rounded bg-red-500/15 text-red-300 text-xs hover:bg-red-500/25 flex items-center gap-1">
                          <X size={12}/> Reject
                        </button>
                      </div>
                    ) : (
                      <span className="text-xs text-slate-400">{it.note || "—"}</span>
                    )}
                  </td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan={6} className="text-center text-slate-500 py-6">No applications</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </AppLayout>
  );
}
